import { useState } from "react";
import type { Company, ThemeClasses } from "../types";
import { brand, brandTextColor } from "../../../../../../config/theme";

interface CompanyDetailsModalProps {
  company: Company;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onClose: () => void;
  onEdit: (company: Company) => void;
  onDelete?: (company: Company) => void;
  onSoftDelete?: (company: Company) => void;
  t: (key: string) => string;
}

export function CompanyDetailsModal({
  company,
  themeClasses,
  isDarkMode,
  onClose,
  onEdit,
  onDelete,
  onSoftDelete,
}: CompanyDetailsModalProps) {
  const [editLoading, setEditLoading] = useState(false);

  const getStatusColor = (status_id?: string) => {
    switch (status_id) {
      case "jXmjzHcBD5E1J0wjwHzq": // facturable
        return "bg-green-100 text-green-700 border-green-500/30 dark:bg-green-900/30 dark:text-green-400";
      case "Y2aD7bE1fF6jM3kO9hP5": // suspended
        return "bg-red-100 text-red-700 border-red-500/30 dark:bg-red-900/30 dark:text-red-400";
      default:
        return "bg-gray-100 text-gray-600 border-gray-400/30 dark:bg-gray-800 dark:text-gray-400";
    }
  };

  const getStatusIcon = (status_id?: string) => {
    switch (status_id) {
      case "jXmjzHcBD5E1J0wjwHzq":
        return "ri-checkbox-circle-line";
      case "Y2aD7bE1fF6jM3kO9hP5":
        return "ri-close-circle-line";
      default:
        return "ri-question-line";
    }
  };

  const getStatusLabel = (status_id: string) => {
    switch (status_id) {
      case "jXmjzHcBD5E1J0wjwHzq": // facturable
        return "Activa";
      case "Y2aD7bE1fF6jM3kO9hP5": // suspended
        return "Suspendida";
      case "D4h7DGEqSEXZcG0ByStX": // deleted
        return "Eliminada";
      default:
        return "Desconocida";
    }
  };

  const sectionBox = `${
    isDarkMode ? "bg-[#3A3A3A]" : "bg-gray-50"
  } border ${themeClasses.cardBorder} rounded-xl p-4`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div
        className={`relative ${themeClasses.cardBg} rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto`}
      >
        {/* Header */}
        <div className={`sticky top-0 z-10 flex items-center p-6 border-b ${themeClasses.cardBorder} ${themeClasses.cardBg}`}>
          <div className="flex items-center space-x-4">
            {company.logo ? (
              <img
                src={company.logo}
                alt={company.name}
                className="w-14 h-14 rounded-xl object-cover"
              />
            ) : (
              <div
                className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                  isDarkMode ? "bg-[#4A4A4A]" : "bg-gray-100"
                }`}
              >
                <i
                  className={`ri-building-2-line text-2xl ${themeClasses.textSecondary}`}
                ></i>
              </div>
            )}
            <div>
              <h2 className={`text-xl font-bold ${themeClasses.text}`}>
                {company.name}
              </h2>
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {company.business_name}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status & Basic Info */}
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded-full border ${getStatusColor(
                company.status_id,
              )}`}
            >
              <i className={getStatusIcon(company.status_id)}></i>
              {getStatusLabel(company.status_id || "")}
            </span>
            {company.rfc && (
              <span
                className={`inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded-full ${
                  isDarkMode ? "bg-[#3A3A3A]" : "bg-gray-100"
                } ${themeClasses.textSecondary}`}
              >
                <i className="ri-bank-card-line"></i>
                RFC: {company.rfc}
              </span>
            )}
            {(company.origin || []).map((o) => (
              <span
                key={o}
                className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400"
              >
                <i className="ri-git-branch-line"></i>
                {o.toUpperCase()}
              </span>
            ))}
          </div>

          {/* Contact Info */}
          <div>
            <h3 className={`text-sm font-semibold ${themeClasses.text} mb-3`}>
              Información de Contacto
            </h3>
            <div
              className={`${sectionBox} space-y-3`}
            >
              <div className="flex items-center space-x-3">
                <i className={`ri-mail-line ${themeClasses.textSecondary}`}></i>
                <span className={themeClasses.text}>{company.email}</span>
              </div>
              {company.phone && (
                <div className="flex items-center space-x-3">
                  <i
                    className={`ri-phone-line ${themeClasses.textSecondary}`}
                  ></i>
                  <span className={themeClasses.text}>{company.phone}</span>
                </div>
              )}
              {company.website && (
                <div className="flex items-center space-x-3">
                  <i
                    className={`ri-global-line ${themeClasses.textSecondary}`}
                  ></i>
                  <a
                    href={company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                    style={{ color: brandTextColor }}
                  >
                    {company.website}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Contact Person */}
          {company.contact_person && (
            <div>
              <h3 className={`text-sm font-semibold ${themeClasses.text} mb-3`}>
                Persona de Contacto
              </h3>
              <div
                className={`${
                  isDarkMode ? "bg-[#3A3A3A]" : "bg-gray-50"
                } rounded-xl p-4 space-y-3`}
              >
                <div className="flex items-center space-x-3">
                  <i
                    className={`ri-user-line ${themeClasses.textSecondary}`}
                  ></i>
                  <span className={themeClasses.text}>
                    {company.contact_person.name}
                  </span>
                </div>
                {company.contact_person.email && (
                  <div className="flex items-center space-x-3">
                    <i
                      className={`ri-mail-line ${themeClasses.textSecondary}`}
                    ></i>
                    <span className={themeClasses.text}>
                      {company.contact_person.email}
                    </span>
                  </div>
                )}
                {company.contact_person.phone && (
                  <div className="flex items-center space-x-3">
                    <i
                      className={`ri-phone-line ${themeClasses.textSecondary}`}
                    ></i>
                    <span className={themeClasses.text}>
                      {company.contact_person.phone}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Address */}
          {company.contact_address && (
            <div>
              <h3 className={`text-sm font-semibold ${themeClasses.text} mb-3`}>
                Dirección
              </h3>
              <div
                className={sectionBox}
              >
                <div className="flex items-start space-x-3">
                  <i
                    className={`ri-map-pin-line ${themeClasses.textSecondary} mt-1`}
                  ></i>
                  <div className={themeClasses.text}>
                    <p>{company.contact_address.address1}</p>
                    {company.contact_address.address2 && (
                      <p>{company.contact_address.address2}</p>
                    )}
                    <p>
                      {company.contact_address.city},{" "}
                      {company.contact_address.state}{" "}
                      {company.contact_address.zip_code}
                    </p>
                    <p>{company.contact_address.country}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`sticky bottom-0 flex items-center justify-end space-x-3 p-6 border-t ${themeClasses.cardBorder} ${themeClasses.cardBg}`}>
          <button
            onClick={onClose}
            className={`px-4 py-2.5 rounded-xl ${themeClasses.buttonHover} ${themeClasses.buttonText} font-medium transition-all`}
          >
            Cerrar
          </button>

          {/* Delete button - only show for active companies */}
          {company.status_id === "jXmjzHcBD5E1J0wjwHzq" && onDelete && (
            <button
              onClick={() => onDelete(company)}
              className="px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-xl transition-all"
            >
              Suspender
            </button>
          )}

          {/* Soft Delete button - show for active and suspended companies */}
          {onSoftDelete && (
            <button
              onClick={() => onSoftDelete(company)}
              className="px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white font-medium rounded-xl transition-all"
            >
              Eliminar
            </button>
          )}

          <button
            onClick={async () => {
              setEditLoading(true);
              try {
                await Promise.resolve(onEdit(company));
              } finally {
                setEditLoading(false);
              }
            }}
            disabled={editLoading}
            className="px-4 py-2.5 font-medium rounded-xl transition-all flex items-center gap-2 disabled:opacity-70 disabled:cursor-wait"
            style={{ backgroundColor: brand.primary, color: brand.accent }}
          >
            {editLoading && <i className="ri-loader-4-line animate-spin"></i>}
            {editLoading ? "Cargando..." : "Editar Empresa"}
          </button>
        </div>
      </div>
    </div>
  );
}
